import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { useTenantModules } from '@/lib/useTenantModules';
import { Button } from '@/components/ui/button';
import { LayoutDashboard, ShoppingCart, Package, Users, FileText, Settings, CreditCard, Shield, LogOut, BarChart3, Cog, ShoppingBag, MessageCircle, UserPlus, Wallet, Store, ClipboardList, Lock, Monitor, Database, Receipt, AlertTriangle, Clock, Tag, FileSignature, DollarSign, Globe, Truck } from 'lucide-react';
import { cn } from '@/lib/utils';
import { RemoteSupportClientWidget } from '@/components/RemoteSupportClientWidget';

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { modules: tenantModules } = useTenantModules();
  const modules = tenantModules || { pdv: true, whatsapp: false, online: false, marketplace: false };
  const [subscription, setSubscription] = useState(null);
  const [renewBannerClosed, setRenewBannerClosed] = useState(false);

  // Buscar módulos do plano do tenant
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const response = await api.get('/saas/subscription');
        setSubscription(response.data);
      } catch (error) {
        // Silenciar erro
      }
    };
    
    if (user) {
      if (user.role !== 'super_admin') {
        fetchSubscription();
      }
    }
  }, [user]);

  // Calcular dias restantes
  const getDaysRemaining = () => {
    if (!subscription?.tenant?.subscription_end) return null;
    const endDate = new Date(subscription.tenant.subscription_end);
    const today = new Date();
    const diffTime = endDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysRemaining = getDaysRemaining();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Menu para usuários normais e admins de empresa com verificação de módulos
  const regularNavigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, module: null },
    { name: 'PDV', href: '/pos', icon: ShoppingCart, module: 'pdv' },
    { name: 'Produtos', href: '/products', icon: Package, module: null },
    { name: 'Etiquetas', href: '/etiquetas', icon: Tag, module: null },
    { name: 'Clientes', href: '/customers', icon: Users, module: null },
    { name: 'Vendas', href: '/sales', icon: FileText, module: null },
    { name: 'Contas a Receber', href: '/contas-a-receber', icon: DollarSign, module: null },
    { name: 'Contas a Pagar', href: '/contas-a-pagar', icon: Wallet, module: null },
    { name: 'Fornecedores', href: '/fornecedores', icon: Truck, module: null },
    { name: 'Propostas', href: '/propostas', icon: FileSignature, module: null },
    { name: 'Recibos de Pagamento', href: '/recibos', icon: Receipt, module: null },
    { name: 'Emitir NF-e', href: '/emitir-nfe', icon: Receipt, module: null },
    { name: 'Gerenciar NF-e', href: '/gerenciar-nfe', icon: FileText, module: null },
    { name: 'Pedidos Online', href: '/pedidos-online', icon: ShoppingBag, module: 'online' },
    { name: 'Marketplaces', href: '/marketplaces', icon: Store, module: 'marketplace' },
    { name: 'WhatsApp Bot', href: '/whatsapp', icon: MessageCircle, module: 'whatsapp' },
    { name: 'Pedidos WhatsApp', href: '/whatsapp/pedidos', icon: ClipboardList, module: 'whatsapp' },
    { name: 'Relatórios', href: '/reports', icon: BarChart3, module: null },
    { name: 'Relatório de Vendas', href: '/relatorio-vendas', icon: DollarSign, module: null },
    { name: 'Relatórios Módulos', href: '/reports/modules', icon: BarChart3, module: null },
    { name: 'Caixa por Módulo', href: '/cashflow/modules', icon: Wallet, module: null },
    { name: 'Relatórios Caixa', href: '/relatorios-caixa', icon: Wallet, module: null },
    { name: 'Usuários', href: '/usuarios', icon: UserPlus, module: null },
    { name: 'Configurações', href: '/configuracoes', icon: Cog, module: null },
    { name: 'Assinatura', href: '/subscription', icon: CreditCard, module: null },
  ];

  // Menu para super_admin (dono do SaaS) - sem restrições de módulo
  const superAdminNavigation = [
    { name: 'Painel Admin', href: '/admin', icon: Shield, module: null },
    { name: 'Parceiros', href: '/admin/parceiros', icon: DollarSign, module: null },
    { name: 'Suporte Remoto', href: '/suporte-remoto', icon: Monitor, module: null },
    { name: 'Migração Firebird', href: '/migracao-firebird', icon: Database, module: null },
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, module: null },
    { name: 'PDV', href: '/pos', icon: ShoppingCart, module: null },
    { name: 'Produtos', href: '/products', icon: Package, module: null },
    { name: 'Etiquetas', href: '/etiquetas', icon: Tag, module: null },
    { name: 'Clientes', href: '/customers', icon: Users, module: null },
    { name: 'Vendas', href: '/sales', icon: FileText, module: null },
    { name: 'Contas a Pagar', href: '/contas-a-pagar', icon: Wallet, module: null },
    { name: 'Fornecedores', href: '/fornecedores', icon: Truck, module: null },
    { name: 'Propostas', href: '/propostas', icon: FileSignature, module: null },
    { name: 'Recibos de Pagamento', href: '/recibos', icon: Receipt, module: null },
    { name: 'Emitir NF-e', href: '/emitir-nfe', icon: Receipt, module: null },
    { name: 'Gerenciar NF-e', href: '/gerenciar-nfe', icon: FileText, module: null },
    { name: 'Pedidos Online', href: '/pedidos-online', icon: ShoppingBag, module: null },
    { name: 'Marketplaces', href: '/marketplaces', icon: Store, module: null },
    { name: 'WhatsApp Bot', href: '/whatsapp', icon: MessageCircle, module: null },
    { name: 'Pedidos WhatsApp', href: '/whatsapp/pedidos', icon: ClipboardList, module: null },
    { name: 'Relatórios', href: '/reports', icon: BarChart3, module: null },
    { name: 'Relatório de Vendas', href: '/relatorio-vendas', icon: DollarSign, module: null },
    { name: 'Relatórios Módulos', href: '/reports/modules', icon: BarChart3, module: null },
    { name: 'Caixa por Módulo', href: '/cashflow/modules', icon: Wallet, module: null },
    { name: 'Relatórios Caixa', href: '/relatorios-caixa', icon: Wallet, module: null },
    { name: 'Usuários', href: '/usuarios', icon: UserPlus, module: null },
    { name: 'Configurações', href: '/configuracoes', icon: Cog, module: null },
  ];

  // Menu para role='partner' (parceiro/afiliado do SaaS)
  const partnerNavigation = [
    { name: 'Painel do Parceiro', href: '/parceiro', icon: DollarSign, module: null },
    { name: 'Meu Minisite', href: '/parceiro/minisite', icon: Globe, module: null },
  ];

  const navigation =
    user?.role === 'super_admin' ? superAdminNavigation :
    user?.role === 'partner' ? partnerNavigation :
    regularNavigation;

  // Verifica se o módulo está habilitado
  const isModuleEnabled = (moduleName) => {
    if (!moduleName) return true; // Módulos null são sempre visíveis
    return modules[moduleName] === true;
  };

  return (
    <div className="flex h-screen bg-slate-50">
      {/* Sidebar */}
      <div className="w-64 bg-slate-900 text-white flex flex-col min-h-screen" data-testid="sidebar">
        <div className="p-6 border-b border-slate-800">
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>nfcesysPdv</h1>
          <p className="text-xs text-slate-400 mt-1">Sistema PDV com NFC-e</p>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navigation.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.href;
            const isEnabled = isModuleEnabled(item.module);
            
            // Se o módulo não está habilitado, mostrar item bloqueado
            if (!isEnabled) {
              return (
                <Link
                  key={item.href}
                  to={`/upgrade/${item.module}`}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-slate-500 hover:text-amber-300 hover:bg-slate-800/60 transition-colors group"
                  title={`Faça upgrade do plano para acessar ${item.name}`}
                  data-testid={`nav-${item.name.toLowerCase()}-locked`}
                >
                  <Icon className="w-5 h-5 opacity-60 group-hover:opacity-100" />
                  <span className="opacity-60 group-hover:opacity-100">{item.name}</span>
                  <Lock className="w-3.5 h-3.5 ml-auto text-amber-500" />
                </Link>
              );
            }
            
            return (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  'flex items-center gap-3 px-4 py-3 rounded-lg font-medium hover:bg-slate-800',
                  isActive ? 'bg-slate-800 text-white' : 'text-slate-300 hover:text-white'
                )}
                data-testid={`nav-${item.name.toLowerCase()}`}
              >
                <Icon className="w-5 h-5" />
                {item.name}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-slate-800 mt-auto">
          {/* Indicador de Licença */}
          {user?.role !== 'super_admin' && daysRemaining !== null && daysRemaining <= 15 && (
            <div 
              onClick={() => navigate('/subscription')}
              className={`mb-3 p-2 rounded-lg cursor-pointer transition-colors ${
                daysRemaining <= 0 ? 'bg-rose-600 hover:bg-rose-700' :
                daysRemaining <= 3 ? 'bg-amber-600 hover:bg-amber-700' :
                daysRemaining <= 7 ? 'bg-yellow-600 hover:bg-yellow-700' :
                'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              <div className="flex items-center gap-2">
                {daysRemaining <= 0 ? (
                  <AlertTriangle className="w-4 h-4 text-white" />
                ) : (
                  <Clock className="w-4 h-4 text-white" />
                )}
                <div className="flex-1">
                  <p className="text-xs text-white font-medium">
                    {daysRemaining <= 0 ? 'Licença expirada!' : `${daysRemaining} dia${daysRemaining !== 1 ? 's' : ''} restantes`}
                  </p>
                  <p className="text-xs text-white/70">
                    {daysRemaining <= 0 ? 'Clique para renovar' : 'Clique para gerenciar'}
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <div className="mb-4">
            <p className="text-sm font-semibold text-white">{user?.name}</p>
            <p className="text-xs text-slate-400">{user?.email}</p>
            <p className="text-xs text-emerald-400 mt-1 capitalize">
              {user?.role === 'super_admin' ? 'Super Admin' : user?.role}
            </p>
          </div>
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-800"
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sair
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        {/* Banner de renovação (aviso antes de vencer) */}
        {user?.role !== 'super_admin' && daysRemaining !== null && daysRemaining <= 7 && !renewBannerClosed && (
          <div
            className={`flex items-center gap-3 px-6 py-3 text-white ${daysRemaining <= 0 ? 'bg-rose-600' : 'bg-amber-600'}`}
            data-testid="renew-banner"
          >
            <AlertTriangle className="w-5 h-5 shrink-0" />
            <p className="text-sm font-medium flex-1">
              {daysRemaining <= 0
                ? 'Sua assinatura venceu. Renove agora para continuar usando o sistema.'
                : `Sua assinatura vence em ${daysRemaining} dia${daysRemaining !== 1 ? 's' : ''}. Renove antes de vencer para não perder o acesso.`}
            </p>
            <Button
              size="sm"
              onClick={() => navigate('/subscription')}
              className="bg-white text-slate-900 hover:bg-slate-100 font-semibold"
              data-testid="renew-banner-btn"
            >
              Renovar agora
            </Button>
            <button
              onClick={() => setRenewBannerClosed(true)}
              className="text-white/80 hover:text-white text-sm px-2"
              data-testid="renew-banner-dismiss"
            >
              Dispensar
            </button>
          </div>
        )}
        {children}
      </div>
      
      {/* Widget de Suporte Remoto para clientes (não super_admin) */}
      {user?.role !== 'super_admin' && <RemoteSupportClientWidget />}
    </div>
  );
};
